import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  yandexCabinetImportApi,
  YandexImportEntityType,
} from '../services/api';

const SPECS: Record<
  YandexImportEntityType,
  { title: string; steps: string[]; columns: string; orderHint: string }
> = {
  persons: {
    title: 'Контрагенты из Яндекс ОРД',
    orderHint: 'Шаг 1 из 5 — начните с контрагентов.',
    columns:
      'external_id*, name*, inn, juridical_type (juridical/ip/physical), phone, address, roles',
    steps: [
      'В ЛК Яндекс ОРД откройте организации / контрагентов.',
      'Скопируйте object_id (external_id), наименование, ИНН, адрес (обязателен для Яндекса).',
      'Скачайте шаблон Excel, заполните строки, загрузите сюда.',
      'Повторная загрузка с тем же external_id обновляет запись.',
    ],
  },
  contracts: {
    title: 'Договоры из Яндекс ОРД',
    orderHint: 'Шаг 2 из 5 — после контрагентов.',
    columns:
      'external_id*, type*, serial, contract_date, amount, client_external_id*, contractor_external_id*, my_role (client/contractor), subject_type, counterparty_inn',
    steps: [
      'В ЛК Яндекс ОРД выгрузите договоры: object_id, номер, дата, стороны (их external_id).',
      'external_id сторон должны совпасть с уже загруженными контрагентами.',
      'my_role = contractor, если вы исполнитель; client — если заказчик.',
      'Скачайте шаблон → заполните → загрузите.',
    ],
  },
  creatives: {
    title: 'Креативы из Яндекс ОРД',
    orderHint: 'Шаг 3 из 5 — после договоров.',
    columns:
      'external_id*, erid*, name*, form, contract_external_id, creative_type (SELF_AD/THIRD_PARTY), kktu, brand, pay_type, price_amount',
    steps: [
      'В ЛК Яндекс ОРД по каждому креативу скопируйте object_id, erid (токен), название.',
      'Для чужой рекламы укажите contract_external_id уже импортированного договора.',
      'После импорта привяжите ссылки на ролики в карточке креатива.',
      'Новые креативы лучше создавать уже в ТрендиОРД — они уйдут в Яндекс автоматически.',
    ],
  },
  platforms: {
    title: 'Площадки из Яндекс ОРД',
    orderHint: 'Шаг 4 из 5 — для сдачи статистики по pad.',
    columns: 'external_id*, title*, url, pad_type (site/apps/smarttv/hbbtv; web→site), person_external_id, person_inn',
    steps: [
      'В ЛК Яндекс ОРД скопируйте object_id площадки, название и URL канала.',
      'Привяжите person_external_id контрагента-издателя (уже в кабинете).',
      'URL нужен для автосбора просмотров (YouTube / VK / Rutube / …).',
      'Новые площадки можно создавать в Тренди — они уйдут upsert в Яндекс.',
    ],
  },
  invoices: {
    title: 'Номера актов из Яндекс ОРД',
    orderHint: 'Шаг 5 из 5 — для уже выставленных актов.',
    columns: 'erid* (или creative_external_id), invoice_external_id*, invoice_serial*, invoice_date',
    steps: [
      'Нужно, если акты уже созданы в Яндекс ОРД, а в Тренди нет номера.',
      'Укажите erid креатива и номер/id акта из ЛК Яндекс.',
      'После этого раздел «Акты» покажет корректный serial.',
      'Новые акты по креативам, созданным в Тренди, формируются автоматически.',
    ],
  },
};

type Props = {
  entityType: YandexImportEntityType;
  onDone?: () => void;
  compact?: boolean;
};

const YandexOrdImportPanel: React.FC<Props> = ({ entityType, onDone, compact }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const spec = SPECS[entityType];

  const upload = async (file: File | null) => {
    if (!file) return;
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const res = await yandexCabinetImportApi.importExcel(entityType, file);
      const msg =
        `Импорт «${entityType}»: +${res.imported}, обновлено ${res.updated}` +
        (res.errors ? `, ошибок ${res.errors}` : '');
      setSuccess(msg);
      if (res.messages?.length) {
        setSuccess(`${msg}. ${res.messages.slice(0, 3).join('; ')}`);
      }
      onDone?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка импорта');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Alert severity="info" sx={{ mb: compact ? 1.5 : 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {spec.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {spec.orderHint} В API Яндекс ОРД нет выгрузки списков — переносим вручную через Excel.
      </Typography>
      {!compact && (
        <>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              {spec.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 1.5, fontFamily: 'monospace' }}>
            Колонки: {spec.columns}
          </Typography>
        </>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      {success && (
        <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
          {success}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => yandexCabinetImportApi.downloadTemplate(entityType).catch((e) =>
            setError(e?.response?.data?.message || e?.message || 'Не удалось скачать шаблон')
          )}
        >
          Скачать шаблон
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={(e) => upload(e.target.files?.[0] || null)}
        />
        <Button
          size="small"
          variant="contained"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : 'Загрузить Excel'}
        </Button>
      </Stack>
      {compact && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Колонки: {spec.columns}
          </Typography>
        </Box>
      )}
    </Alert>
  );
};

export default YandexOrdImportPanel;
